import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { differenceBy, isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Modal } from 'react-bootstrap';
import { v4 as uuidV4 } from 'uuid';
import { useQueryClient } from '@tanstack/react-query';

import {
    DOCUMENT_REQUEST_FORM,
    DOCUMENT_REQUEST_FORM as DRF,
    LOCAL_STORAGE,
    MODAL,
    MY_DOCUMENTS,
    QUERY_KEYS,
} from '../../../constants';
import SSCSelect from '../../shared/SSCSelect';
import {
    extractFileName,
    getCustomerByID,
    getHiringClients,
    getLocalStorageItem,
    popup,
    renameFileWithUUID,
} from '../../../utils';
import useWindowSize from '../../../hooks/useWindowSize';
import { useDebounce } from '../../../hooks';
import { DocImage, FeedbackCross, IconPlus, ViewIcon } from '../../../assets/svg';
import {
    setFileViewModalDetails,
    setLoaderProgress,
    setLoaderVisibility,
    setShowProgress,
} from '../../../redux';
import {
    createMyDocumentsAPI,
    getAllMyDocumentsAPI,
    signedMyDocumentAPI,
} from '../../../utils/apiServices/myDocumentsAPI';

const allowedTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.ms-excel', // .xls
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'image/png',
    'image/jpeg',
];

const UploadMyDocumentModal = ({
    showModal,
    setShowModal,
    folderId,
    folderName,
}) => {
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const currentAccountId = getLocalStorageItem(LOCAL_STORAGE.CUSTOMER_ID);
    const selectedAccountName = useSelector((state) => state.accountArray.selectedAccountName);
    const { width } = useWindowSize();
    const [tpa, setTpa] = useState([]);
    const [hiringClient, setHiringClient] = useState([]);
    const [selectedHiringClient, setSelectedHiringClient] = useState('');
    const [selectedTpa, setSelectedTpa] = useState('');
    const [hiringClientSearch, setHiringClientSearch] = useState('');
    const [fetchingPT, setFetchingPT] = useState(false);
    const [fetchingHC, setFetchingHC] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);

    async function fetchCustomerData() {
        try {
            setFetchingPT(true);
            const response = await getCustomerByID({
                customerID: currentAccountId,
                fetchPlatform: true,
                fetchHiringClient: false,
                fetchCSM: false,
            });
            if (response?.data) {
                setTpa(response.data[0]?.platforms);
            }
        } catch (error) {
            popup('error', error.message);
        } finally {
            setFetchingPT(false);
        }
    }

    useEffect(() => {
        if (currentAccountId) {
            fetchCustomerData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentAccountId]);

    const debouncedFetchHiringClientData = useDebounce(async () => {
        try {
            setFetchingHC(true);
            const hiringClient = await getHiringClients({
                account_id: currentAccountId,
                platform_id: selectedTpa?.id,
                search: hiringClientSearch,
            });
            setHiringClient(hiringClient?.data);
        } catch (error) {
            popup('error', error.message);
        } finally {
            setFetchingHC(false);
        }
    }, 500);

    useEffect(() => {
        if (!isEmpty(selectedTpa)) {
            debouncedFetchHiringClientData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTpa, hiringClientSearch]);

    const handleRemoveFile = (indexToRemove) => {
        !!selectedFiles.length &&
            setSelectedFiles((prevSelectedFiles) =>
                prevSelectedFiles.filter((_, index) => index !== indexToRemove),
            );
    };

    const onFileChange = (event) => {
        try {
            const files = event.target.files;
            let notAllowedFiles = [];

            if (files.length > 30) {
                return popup('error', 'Maximum thirty(30) files are allowed at once.');
            }

            let selectedFilesArray = Array.from(files);
            for (const file of selectedFilesArray) {
                if (
                    !allowedTypes.includes(file.type) ||
                    file.size > DRF.FILE.FILE_SIZE_LIMIT_25MB
                ) {
                    notAllowedFiles.push(file);
                }
            }

            if (notAllowedFiles?.length > 0) {
                popup(
                    'error',
                    selectedFilesArray.length === 1
                        ? `File should be max 25MB and only 'pdf, doc, png, jpg, jpeg, xls & xlsx are allowed'.`
                        : `Some files have been removed because each file should be max '25MB' and only 'pdf, doc, png, jpg, jpeg, xls & xlsx are allowed'.`,
                );
                selectedFilesArray = differenceBy(selectedFilesArray, notAllowedFiles, 'name');
            }
            selectedFilesArray.forEach((file) => {
                // (if file names are unique) and  Check for duplicate filenames (already uploaded)
                if (
                    selectedFiles.some(
                        (uploadedFile) =>
                            uploadedFile.name === file.name && uploadedFile.size === file.size,
                    )
                ) {
                    throw new Error(
                        `Document "${file.name}" already selected. Please choose unique files.`,
                    );
                }
            });
            setSelectedFiles((prevSelectedFiles) => [...prevSelectedFiles, ...selectedFilesArray]);
        } catch (error) {
            popup('error', error.message);
        }
    };

    // 1.) To dynamically creates the <input> element,
    // 2.) Don't need the input to persist in the DOM but only need its functionality.
    const openFileExplorer = () => {
        if (selectedFiles.length >= 30) {
            return popup('error', 'Upto thirty(30) files are allowed.');
        } else {
            const input = document.createElement('input');
            input.type = 'file';
            input.multiple = true;
            input.onchange = onFileChange;
            input.accept = '.pdf,.doc,.xls,.xlsx,.png,.jpg,.jpeg';
            input.click();
        }
    };

    const handlePreview = (i) => {
        const currentFile = selectedFiles[i];
        const fileType = currentFile?.type;
        const uri = URL.createObjectURL(currentFile);
        dispatch(
            setFileViewModalDetails({
                openFileViewerModal: true,
                docs: [{ uri, fileType }],
                fileName: currentFile?.name,
            }),
        );
    };

    const readFileAsArrayBuffer = async (file) => {
        try {
            const reader = new FileReader();
            reader.readAsArrayBuffer(file);
            return new Promise((resolve, reject) => {
                reader.onload = () => resolve(reader.result);
                reader.onerror = (error) => reject(error);
            });
        } catch (error) {
            return Promise.reject(error);
        }
    };

    const onUploadClick = async () => {
        const data = [];
        const filesData = [];
        const totalFiles = selectedFiles.length;
        let uploadedCount = 0;

        try {
            const { count, maximumDocumentsPerAccountLimit = 100 } = await getAllMyDocumentsAPI({
                account_id: currentAccountId,
                all: true,
            });

            if (count >= maximumDocumentsPerAccountLimit) {
                return popup(
                    'error',
                    `You have reached the maximum limit of ${maximumDocumentsPerAccountLimit} documents in your Cloud account. First, please delete documents from FOLDERS or RECYCLED DOCUMENTS before uploading new documents`,
                    7000,
                );
            }

            if (count + selectedFiles.length > maximumDocumentsPerAccountLimit) {
                return popup(
                    'error',
                    `You are exceeding the maximum limit of ${maximumDocumentsPerAccountLimit} documents in your Cloud account. You may upload ${maximumDocumentsPerAccountLimit - count} more documents until you have reached this limit`,
                    7000,
                );
            }

            dispatch(setLoaderVisibility(true));
            dispatch(setShowProgress(true));
            /// UPLOAD FILE PARALLELS
            const uploadPromises = selectedFiles.map(async (file) => {
                const fileUUID = uuidV4();
                const signedData = {
                    file_name: renameFileWithUUID(file?.name, fileUUID),
                    account_id: currentAccountId,
                    account_name: selectedAccountName,
                };

                const {
                    data: { preSignedUrl, filePath },
                } = await signedMyDocumentAPI({ signedData });

                const binaryData = await readFileAsArrayBuffer(file);
                const uploadResponse = await fetch(preSignedUrl, {
                    method: 'PUT',
                    body: binaryData,
                    headers: {
                        'Content-Type': file?.type,
                    },
                });

                if (uploadResponse.status === 200) {
                    uploadedCount += 1;
                    dispatch(setLoaderProgress(Math.round((uploadedCount / totalFiles) * 100)));

                    filesData.push({
                        file_name: extractFileName(file?.name),
                        s3_key: filePath,
                    });
                } else {
                    throw new Error(`${file.name} upload failed`);
                }
            });
            await Promise.all(uploadPromises);

            // SAVE TO BE
            for (const file of filesData) {
                data.push({
                    account_id: currentAccountId,
                    s3_key: file?.s3_key,
                    file_name: file?.file_name,
                    folder_id: folderId,
                    platform_id: selectedTpa?.id,
                    hiring_client_id: selectedHiringClient?.id,
                });
            }
            if (data.length > 0) {
                await createMyDocumentsAPI({ body: data });
                setSelectedFiles([]);
                setShowModal(false);
                queryClient.invalidateQueries({
                    queryKey: [QUERY_KEYS.MY_DOCUMENTS],
                    exact: false,
                });
            }
        } catch (err) {
            popup('error', err.message);
        } finally {
            dispatch(setLoaderVisibility(false));
            dispatch(setShowProgress(false));
        }
    };

    return (
        <Modal
            show={showModal}
            onHide={() => {
                setShowModal(false);
                // dispatch(setSelectedHiringClients([]));
            }}
            size='lg'
            dialogClassName='overflow-hidden'
            fullscreen='lg-down'
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title className='me-3' style={{ fontSize: '18px' }}>
                    {MY_DOCUMENTS.MODAL.HEADING + `${folderName}`}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className='d-flex gap-3 w-100 flex-nowrap'>
                    <SSCSelect
                        placeholder={DRF.PLATFORM.PLACEHOLDER}
                        label={DRF.PLATFORM.LABEL}
                        options={tpa?.map((pt) => ({
                            label: pt?.name,
                            value: pt,
                        }))}
                        onChange={(v) => {
                            setSelectedTpa(!isEmpty(v) ? v?.value : null);
                            setSelectedHiringClient(null);
                        }}
                        isClearable={true}
                        menuPlacement={width < 768 ? 'top' : 'auto'}
                        value={
                            !isEmpty(selectedTpa)
                                ? {
                                      label: selectedTpa?.name,
                                      value: selectedTpa,
                                  }
                                : null
                        }
                        isLoading={fetchingPT}
                    />
                    <SSCSelect
                        label={DRF.HIRING_CLIENT.LABEL}
                        placeholder={DRF.HIRING_CLIENT.PLACEHOLDER}
                        options={hiringClient?.map((data) => ({
                            label: data?.name,
                            value: data,
                        }))}
                        onChange={(v) => setSelectedHiringClient(!isEmpty(v) ? v?.value : null)}
                        onInputChange={(v) => {
                            if (v) {
                                setHiringClientSearch(v);
                            }
                        }}
                        isClearable={true}
                        menuPlacement={width < 768 ? 'top' : 'auto'}
                        value={
                            selectedHiringClient
                                ? {
                                      label: selectedHiringClient?.name,
                                      value: selectedHiringClient,
                                  }
                                : null
                        }
                        isDisabled={isEmpty(selectedTpa) || isEmpty(hiringClient)}
                        isLoading={fetchingHC}
                    />
                </div>
                <div className='d-flex flex-column mt-3 align-items-start justify-content-start'>
                    {selectedFiles?.length === 0 && (
                        <button
                            className='ssc-primary-green-btn upload-file-btn rounded-1'
                            style={{ fontSize: '15px', width: 'auto' }}
                            type='button'
                            onClick={openFileExplorer}
                            disabled={
                                selectedFiles.length === DOCUMENT_REQUEST_FORM.FILE.FILE_LIMIT
                            }
                            title={'Select Files'}
                        >
                            {'Select Files'}
                        </button>
                    )}
                    {selectedFiles?.length > 0 && (
                        <div className='w-100 mb-2 ms-2 d-flex align-align-items-start text-black-50'>
                            <span>{`Total Selected Files ${selectedFiles.length}`}</span>
                        </div>
                    )}
                    {selectedFiles?.length > 0 && (
                        <div className='d-flex align-items-center file-upload mb-2'>
                            {selectedFiles?.map((file, index) => (
                                <div
                                    key={index}
                                    className='d-flex align-items-start flex-column position-relative'
                                >
                                    <span title={file.name}>
                                        <DocImage />
                                    </span>
                                    {file.name.length < 10 ? (
                                        <span
                                            title={file.name}
                                            className='mt-2'
                                            style={{ fontSize: '13px' }}
                                        >
                                            {file.name}
                                        </span>
                                    ) : (
                                        <span
                                            className='truncate-text mt-2'
                                            title={file.name}
                                            style={{ fontSize: '13px' }}
                                        >
                                            {file.name.slice(0, 6) +
                                                '...' +
                                                file.name.split('.')[1]}
                                        </span>
                                    )}
                                    <button
                                        id='action-btn'
                                        className='remove-file-btn position-absolute'
                                        onClick={() => handleRemoveFile(index)}
                                        type='button'
                                        title='Remove'
                                    >
                                        <FeedbackCross />
                                    </button>
                                    <span
                                        className='position-absolute'
                                        style={{
                                            bottom: '20px',
                                            right: '-5px',
                                            background: 'white',
                                        }}
                                        id='action-btn'
                                        onClick={() => handlePreview(index)}
                                        title='View'
                                        type='button'
                                    >
                                        <ViewIcon />
                                    </span>
                                </div>
                            ))}
                            {selectedFiles?.length > 0 && (
                                <span className='ms-3'>
                                    <button
                                        id='action-btn-green'
                                        type='button'
                                        onClick={openFileExplorer}
                                    >
                                        <IconPlus />
                                    </button>
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <div className='d-flex gap-3 w-100 align-items-center justify-content-end modal-btn mt-3'>
                    <Button
                        type='button'
                        className='ssc-secondary-white-btn '
                        onClick={() => {
                            setShowModal(false);
                        }}
                        style={{ height: '40px', fontSize: '15px' }}
                        title={MODAL.CANCEL_BUTTON}
                    >
                        {MODAL.CANCEL_BUTTON}
                    </Button>
                    <span
                        title={selectedFiles.length === 0 ? 'Select at least one file' : 'Upload'}
                    >
                        <Button
                            type='submit'
                            onClick={onUploadClick}
                            className='table-footer-btn'
                            disabled={selectedFiles.length === 0}
                            style={{ height: '40px' }}
                        >
                            {'Upload'}
                        </Button>
                    </span>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

UploadMyDocumentModal.propTypes = {
    showModal: PropTypes.bool.isRequired,
    setShowModal: PropTypes.func.isRequired,
};

export default UploadMyDocumentModal;
